import { create } from 'zustand';

/**
 * Mount state of the SHELL's primary MFE host (the one rendered by the
 * `/{clientId}/app-container/...` routes — not popups). Two shell behaviours key
 * off it:
 *
 *   - Bug #8: while an MFE is still downloading + bootstrapping, a full-viewport
 *     blur overlay blocks the nav rail so the user can't start a second app load
 *     mid-flight (which raced the loader and left the new app half-mounted).
 *   - Bug #7: the Home footer must appear only once the Home MFE has fully
 *     loaded, not during the transient `/` → `/{clientId}/app-container/home`
 *     redirect (where it flashed before any content).
 *
 * `begin(appId)` is called when the host (re)mounts for an appId; `end(appId)`
 * when that app reports loaded (success OR error — MfeOutlet fires `onLoaded`
 * in both). `end` ignores a stale appId so a late callback from a superseded
 * app can't clear the loading state of the one now mounting.
 */
interface MfeMountState {
  loading: boolean;
  appId: string;
  begin: (appId: string) => void;
  end: (appId: string) => void;
}

export const useMfeMountStore = create<MfeMountState>((set) => ({
  loading: false,
  appId: '',
  begin: (appId) => set({ loading: true, appId }),
  end: (appId) => set((state) => (state.appId === appId ? { ...state, loading: false } : state)),
}));
